import * as React from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PublicIcon from '@mui/icons-material/Public';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Box, IconButton, MenuItem, MenuList, Popover, Tooltip, styled } from "@mui/material";
import { LinkProps, useNavigate } from "react-router-dom";
import ApplicationAppService from '../application/platform/application-app-service';

const listItemButtonStyle = ({ theme }: { theme: any }) => ({
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.5, 1.5),
  padding: theme.spacing(1.25, 2),
  transition: 'all 0.2s ease-in-out',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.contrastText,
    },
  },
});

const ListItemButtonLink = styled(ListItemButton)<LinkProps>(listItemButtonStyle);

const ListItemButtonToggle = styled(ListItemButton)(listItemButtonStyle);

const applicationData = ApplicationAppService.getApplicationData();

const slugifyCityName = (city: string) =>
  city.normalize('NFKD').replace(/\p{Diacritic}/gu, '').toLowerCase().replace(/[^a-z0-9]/g, '');

function getTransparencyPortalPath(): string | undefined {
  const authenticationData = ApplicationAppService.getAuthenticationData();
  const company = authenticationData?.company;
  if (!company?.state || !company?.city) return undefined;

  const uf = company.state.toLowerCase();
  const entityPrefix = authenticationData?.type === 2 ? 'cm' : 'pm';
  const citySlug = `${entityPrefix}${slugifyCityName(company.city)}`;

  return `/${uf}/${citySlug}/transparencia`;
}

function NavTooltip({ title, open, children }: { title: string; open: boolean; children: React.ReactElement }) {
  return (
    <Tooltip title={title} placement="right" disableHoverListener={open}>
      {children}
    </Tooltip>
  );
}

export function MainListItems({ open }: { open: boolean }) {
  const navigate = useNavigate();
  const [budgetMenuAnchor, setBudgetMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [budgetMenuPosition, setBudgetMenuPosition] = React.useState<{ top: number; left: number } | null>(null);

  const handleOpenBudgetMenu = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setBudgetMenuPosition({ top: rect.top, left: rect.right + 8 });
    setBudgetMenuAnchor(event.currentTarget);
  };

  const handleCloseBudgetMenu = () => {
    setBudgetMenuAnchor(null);
    setBudgetMenuPosition(null);
  };

  const handleNavigateFromBudgetMenu = (path: string) => {
    handleCloseBudgetMenu();
    navigate(path);
  };

  return (
    <React.Fragment>
      {applicationData.moduleId === 1 && (
        <React.Fragment>
          <NavTooltip title="Dashboard" open={open}>
            <ListItemButtonLink to="/dashboard">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Remessas" open={open}>
            <ListItemButtonLink to="/remessas-publicacao">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <FileUploadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Remessas"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Diário Oficial" open={open}>
            <ListItemButtonLink to="/diario-oficial">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <FileCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Diário Oficial"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              />
            </ListItemButtonLink>
          </NavTooltip>
        </React.Fragment>
      )}
      {applicationData.moduleId === 2 && (
        <React.Fragment>
          <NavTooltip title="Dashboard" open={open}>
            <ListItemButtonLink to="/dashboard">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Unidades" open={open}>
            <ListItemButtonLink to="/unidades-entidade">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <AccountTreeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Unidades"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Intenção de Registro de Preço" open={open}>
            <ListItemButtonLink to="/intencao-registro-preco">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <PriceCheckIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Int. Registro de Preço"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Compras e Licitações" open={open}>
            <ListItemButtonLink to="/compras-licitacoes">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <ShoppingCartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Compras e Licitações"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Contratos" open={open}>
            <ListItemButtonLink to="/contratos">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <DescriptionIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Contratos"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              />
            </ListItemButtonLink>
          </NavTooltip>
        </React.Fragment>
      )}
      {applicationData.moduleId === 3 && (
        <React.Fragment>
          <NavTooltip title="Dashboard" open={open}>
            <ListItemButtonLink to="/dashboard">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Partidos Políticos" open={open}>
            <ListItemButtonLink to="/partidos-politicos">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <GroupsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Partidos Políticos"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Parlamentares" open={open}>
            <ListItemButtonLink to="/parlamentares">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Parlamentares"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Fornecedores" open={open}>
            <ListItemButtonLink to="/fornecedores">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <BusinessIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Fornecedores"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Emendas" open={open}>
            <ListItemButtonLink to="/emendas">
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <AccountBalanceIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Emendas"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <NavTooltip title="Orçamento" open={open}>
            <ListItemButtonToggle onClick={handleOpenBudgetMenu} selected={Boolean(budgetMenuAnchor)}>
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <AccountBalanceWalletIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Orçamento"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
              <ChevronRightIcon fontSize="small" />
            </ListItemButtonToggle>
          </NavTooltip>
          <Popover
            open={Boolean(budgetMenuAnchor) && budgetMenuPosition !== null}
            onClose={handleCloseBudgetMenu}
            anchorReference="anchorPosition"
            anchorPosition={budgetMenuPosition ?? undefined}
            disableScrollLock
            PaperProps={{
              elevation: 3,
              sx: { minWidth: 240, py: 0.5 },
            }}
            sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
          >
            <MenuList>
              <MenuItem onClick={() => handleNavigateFromBudgetMenu('/unidades-orcamentarias')} sx={{ py: 1.25, px: 2 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AccountTreeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Unidades Orçamentárias"
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                />
              </MenuItem>
              <MenuItem onClick={() => handleNavigateFromBudgetMenu('/programas-orcamentarios')} sx={{ py: 1.25, px: 2 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ListAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Programas Orçamentários"
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                />
              </MenuItem>
              <MenuItem onClick={() => handleNavigateFromBudgetMenu('/acoes-orcamentarias')} sx={{ py: 1.25, px: 2 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <TrackChangesIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Ações Orçamentárias"
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                />
              </MenuItem>
            </MenuList>
          </Popover>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export function SecondaryListItems({ logout, open }: { logout: () => void; open: boolean }) {
  const [transparencyLinkCopied, setTransparencyLinkCopied] = React.useState(false);
  const transparencyPortalPath = getTransparencyPortalPath();

  const handleCopyTransparencyLink = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!transparencyPortalPath) return;
    const url = `${window.location.origin}${transparencyPortalPath}`;
    navigator.clipboard.writeText(url).then(() => {
      setTransparencyLinkCopied(true);
      setTimeout(() => setTransparencyLinkCopied(false), 2000);
    });
  };

  return (
    <React.Fragment>
      {applicationData.moduleId === 3 && transparencyPortalPath && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <NavTooltip title="Painel da Transparência" open={open}>
            <ListItemButtonLink to={transparencyPortalPath} target="_blank" rel="noopener noreferrer" sx={{ flex: 1, minWidth: 0 }}>
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <PublicIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Painel da Transparência"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500, noWrap: true }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <Tooltip title={transparencyLinkCopied ? 'Link copiado!' : 'Copiar link'}>
            <IconButton size="small" onClick={handleCopyTransparencyLink} sx={{ mr: 1.5 }}>
              {transparencyLinkCopied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <NavTooltip title="Sair" open={open}>
        <ListItemButton
          onClick={logout}
          sx={{
            borderRadius: 1,
            margin: '4px 12px',
            padding: '10px 16px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Sair"
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: 500
            }}
          />
        </ListItemButton>
      </NavTooltip>
    </React.Fragment>
  );
}
